import cn from 'clsx'

// Components
import { Section } from '@tracklib/kit'
import { DotsLabel } from 'components/kit/contextmenu/contextmenu'
import Status, { getSharingStatusString } from 'components/usersongs/song/status'
import LicenseButton from 'components/track/licensebutton'
import WarningBox from 'components/usersong/warningbox'
import Dropdown from 'components/usersong/dropdown'

// Types
import { UserSong, Song } from 'types'

// Utils
import formatDate from 'lib/formatdate'
import styles from './header.css'

const HeaderItem = ({ title, text, disabled }: { title: string; text: string; disabled?: boolean }) => (
  <div className={styles.item}>
    <p className={styles.itemTitle}>{title}</p>
    <p className={cn(styles.itemText, disabled && styles.itemTextDisabled)}>{text}</p>
  </div>
)

const getLicenseActivationDateText = (unlicensed: boolean, license_activation_date: string): string => {
  if (unlicensed) {
    return 'Unlicensed'
  }
  if (license_activation_date) {
    return formatDate(license_activation_date)
  }
  return 'Not yet activated'
}

type UserSongHeaderProps = {
  userSong: UserSong
}

const UserSongHeader = ({ userSong }: UserSongHeaderProps): JSX.Element => {
  const { id, state, song_title, license_activation_date, licensee, samples } = userSong
  const unlicensed = state === 'UNLICENSED'

  return (
    <header>
      <Section size="medium" className={styles.header} contentClassName={styles.headerContent}>
        <div className={styles.imageWrapper}>
          <span className={styles.squares1} />
          <span className={styles.squares2} />
          <div className={styles.image} />
        </div>
        <section>
          <p className={styles.titleDesc}>Song</p>
          <h1 className={styles.title}>
            <span>{song_title}</span>
          </h1>
          <footer className={styles.footer}>
            <div className={styles.info}>
              <Status size="medium" invertedVersion status={userSong.state} hideSharing wrapperClassName={styles.statusWrapper} />
              <HeaderItem title="Sharing status" text={getSharingStatusString(state)} />
              <HeaderItem title="Licensee" text={unlicensed ? 'Unlicensed' : licensee} disabled={unlicensed} />
              <HeaderItem
                title="License activation date"
                text={getLicenseActivationDateText(unlicensed, license_activation_date)}
                disabled={unlicensed || !license_activation_date}
              />
            </div>
            <div className={styles.buttonWrapper}>
              {unlicensed ? (
                <div>
                  <LicenseButton
                    variant="white"
                    size="small"
                    song={samples[0].song as Song}
                    userSong={userSong}
                    className={styles.licenseButton}
                  />
                </div>
              ) : (
                <div />
              )}
              <Dropdown
                userSongId={id}
                isUnlicensed={unlicensed}
                licenseOrder={userSong?.samples[0].license_order}
                userSongState={userSong.state}
                customButton={
                  <div className={styles.moreButton}>
                    <DotsLabel className={styles.dots} />
                  </div>
                }
              />
            </div>
          </footer>
        </section>
      </Section>
      <WarningBox userSongId={id} state={state} />
    </header>
  )
}

export default UserSongHeader
